import { useEffect, useState } from 'react'

import { OLMSApiService } from '@/shared/apis'
import { Modal } from '@/shared/components'
import { DialogComponentProps } from '@/shared/components/Modal/models/DialogStructure'
import { Box, styled, Typography } from '@mui/material'

export type ViewCSRDialogProps = DialogComponentProps & {
  onClose: () => void
}

export const ViewCSRDialog: React.FC<ViewCSRDialogProps> = ({
  open,
  onClose,
  title,
  orderId,
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let currentUrl: string | null = null

    const fetchPDF = async () => {
      try {
        setLoading(true)

        const blob = await OLMSApiService.post<Blob>('/order/csr', { id: orderId }, { responseType: 'blob' })

        if (!blob) {
          throw new Error('Failed to fetch PDF')
        }

        const url = URL.createObjectURL(blob)

        setPdfUrl(url)
        setError(null)
        currentUrl = url
      } catch (err) {
        setError('Failed to load PDF. Please try again.')
        console.error('Error loading PDF:', err)
      } finally {
        setLoading(false)
      }
    }

    if (!open || !orderId) return
    fetchPDF()

    return () => {
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl)
      }
    }
  }, [open, orderId])

  return (
    <Modal
      title={title}
      onClose={onClose}
      open={open}
      transition="opacity"
      showBackground={true}
      slotProps={{
        modalMain: {
          sx: {
            padding: '0 !important',
            overflowY: 'hidden !important',
          },
        },
      }}
    >
      <StyledContent>
        {loading && <Box>
          <Typography variant={'body1'}>
            Loading...
          </Typography>
        </Box>}
        {error && <ErrorContainer>{error}</ErrorContainer>}
        {pdfUrl && !error && (
          <PDFContainer>
            <object
              data={pdfUrl}
              type="application/pdf"
              width="100%"
              height="100%"
            >
              <iframe
                src={pdfUrl}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
              >
                This browser does not support PDFs. Please download the PDF to view it.
              </iframe>
            </object>
          </PDFContainer>
        )}
      </StyledContent>
    </Modal>
  )
}

const StyledContent = styled('div')`
  width: 100%;
  ${(props) => props.theme.breakpoints.up('sm')} {
    min-width: 80vw;
  }
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 80vh;
`

const PDFContainer = styled('div')`
  flex: 1;
  width: 100%;
  overflow: hidden;
  background-color: ${props => props.theme.palette.grey[100]};
`

const ErrorContainer = styled('div')`
  color: ${(props) => props.theme.palette.error.main};
  text-align: center;
  padding: 16px;
`